import React, { ChangeEvent, Component } from 'react';
import CustomTabv2 from '../../../custom/CustomTabv2';
import ApiParametersComponent from './parameters/ApiParametersComponent';
import ApiAuthorizationComponent from './auth/ApiAuthorizationComponent';
import ApiHeadersComponent from './headers/ApiHeadersComponent';
import ApiBodyComponent from './body/ApiBodyComponent';
import { RootState } from '../../../../redux/store';
import { changeApiVariable, changeApiVariableAuth } from '../../../../redux/actions/ApiVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { ApiVariableParameter } from '../../../../domain/remote/httpVariable/HttpVariableDomains';
import DataTypeSelectBoxComponent from '../../../custom/DataTypeSelectBoxComponent';
import FormicaLabel from '../../../custom/FormicaLabel';
import FormicaInput from '../../../custom/FormicaInput';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import HttpMethod from '../../../../constant/HttpMethod';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../../../../constant/CollectionNames';
import { requireNonUndefined } from '../../../../util/Assert';
import { ResponseFormats } from '../../../../constant/ResponseFormats';
import DataType from 'constant/DataType';
import RoleHelper from '../../../../RoleHelper';
import LanguageUtil from '../../../../util/LanguageUtil';
import QueryBuilder = query.QueryBuilder;
import ComponentName from '../../../../constant/ComponentName';

class ApiEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private structSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      apiVariable: { apiVariable },
    } = this.props;

    return (
      <div className={'grid grid-cols-5 w-full h-full'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-3'}>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
            <FormicaSearchSelectBox
              options={this.getTransactionOptions()}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              value={apiVariable.transaction}
              rounded={true}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.channel', defaultMessage: 'Channel' })}
              label={LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' })}
              data-testid={`${ComponentName.API_VARIABLE}_channel_select`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.input.placeholder.dataType', defaultMessage: 'Data Type' })} />
            <DataTypeSelectBoxComponent
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              properties={apiVariable.properties}
              onChangeValue={(e) => this.onChangeDataType(e)}
              data-testid={`${ComponentName.API_VARIABLE}_data_type_select`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.responseFormat', defaultMessage: 'Response Format' })} />
            <FormicaSearchSelectBox
              options={this.getResponseFormats()}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeResponseType(e)}
              value={apiVariable.responseType}
              rounded={true}
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              placeholder={'Select a response format'}
              label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
              data-testid={`${ComponentName.API_VARIABLE}_response_format_select`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })} />
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              type={'number'}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeout', defaultMessage: 'Timeout (ms)' })}
              value={apiVariable.timeout}
              onChange={(e) => this.onChangeTimeout(e)}
              data-testid={`${ComponentName.API_VARIABLE}_timeout_input`}
            />
          </div>
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-4'}>
          <div className={'flex gap-x-2'}>
            <div className={'flex w-[120px]'}>
              <FormicaSearchSelectBox
                disabled={!RoleHelper.canEdit('EnrichmentApi')}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.method', defaultMessage: 'Method' })}
                value={apiVariable.method}
                options={this.getMethodTypes()}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeMethod(e)}
                data-testid={`${ComponentName.API_VARIABLE}_method_select`}
              />
            </div>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              value={apiVariable.url}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeUrl(e)}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.url', defaultMessage: 'URL' })}
              data-testid={`${ComponentName.API_VARIABLE}_url_input`}
            />
          </div>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.structSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription, this.structSubscription]);
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready() && this.structSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      this.setState({ transactions, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.structSubscription?.stop();
    this.computation?.stop();
  };

  private getTransactionOptions() {
    return this.state.transactions.map((transaction) => {
      return {
        label: transaction.name,
        value: transaction.name,
      };
    });
  }

  private getResponseFormats = () => {
    return [
      {
        label: LanguageUtil.get({ id: 'common.label.json', defaultMessage: 'JSON' }),
        value: ResponseFormats.JSON,
      },
      {
        label: LanguageUtil.get({ id: 'common.label.xml', defaultMessage: 'XML' }),
        value: ResponseFormats.XML,
      },
      {
        label: LanguageUtil.get({ id: 'common.label.text', defaultMessage: 'Text' }),
        value: ResponseFormats.TEXT,
      },
    ];
  };

  private onChangeMethod(e: FormicaSelectBoxItem) {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    apiVariable.method = e.value as HttpMethod;
    changeApiVariable(apiVariable);
  }

  private onChangeDataType(e: FormicaSelectBoxItem): void {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    apiVariable.properties = e.properties || { dataType: DataType.STRING };
    changeApiVariable(apiVariable);
  }

  private onChangeResponseType(e: FormicaSelectBoxItem) {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    apiVariable.responseType = e.value as ResponseFormats;
    changeApiVariable(apiVariable);
  }

  private getMethodTypes = () => {
    return [
      {
        label: 'GET',
        value: HttpMethod.GET,
      },
      {
        label: 'POST',
        value: HttpMethod.POST,
      },
      {
        label: 'PUT',
        value: HttpMethod.PUT,
      },
      {
        label: 'DELETE',
        value: HttpMethod.DELETE,
      },
      {
        label: 'PATCH',
        value: HttpMethod.PATCH,
      },
    ];
  };

  private getUniqueListBy(arr: any[], key: string) {
    return [...new Map(arr.map((item) => [item[key], item])).values()];
  }

  private getKeyAndValues(url: string) {
    // this function return url params key and values
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    let urlParams = url.split(regex);
    urlParams = urlParams.filter((item) => item != '');
    urlParams.shift();
    return urlParams;
  }

  private addNewParameter(url: string[]) {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    for (let i = 0; i < url.length; i++) {
      if (url.length % 2 == 0) {
        const parameter: ApiVariableParameter = {
          key: url[i],
          value: url[i + 1],
          description: '',
          isActive: true,
        };
        apiVariable.parameters = this.getUniqueListBy([...apiVariable.parameters, parameter], 'key');
        i++;
      }
    }
    changeApiVariable(apiVariable);
  }

  private onChangeUrl(e: ChangeEvent<HTMLInputElement>) {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    const oldUrl = this.getKeyAndValues(apiVariable.url);
    apiVariable.url = e.currentTarget.value;
    const url = this.getKeyAndValues(apiVariable.url);
    // if parameter key change at url input don't append new params
    oldUrl.map((item, index) => {
      if (item != url[index]) {
        apiVariable.parameters = apiVariable.parameters.filter((param) => param.key != item);
      }
    });
    changeApiVariable(apiVariable);
    this.addNewParameter(url);
  }

  private onChangeTimeout = (e: any) => {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    apiVariable.timeout = e.target.value;
    changeApiVariable(apiVariable);
  };

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      apiVariable: { apiVariable },
      changeApiVariable,
    } = this.props;
    apiVariable.transaction = e.value as string;
    changeApiVariable(apiVariable);
  }

  private getTabs = () => {
    return [
      {
        key: 'Parameters',
        label: LanguageUtil.get({ id: 'common.tabs.parameters', defaultMessage: 'Parameters' }),
        content: <ApiParametersComponent />,
        testid: `${ComponentName.API_VARIABLE}_parameters_tab`,
      },
      {
        key: 'Authorization',
        label: LanguageUtil.get({ id: 'common.tabs.authorization', defaultMessage: 'Authorization' }),
        content: <ApiAuthorizationComponent />,
        testid: `${ComponentName.API_VARIABLE}_auth_tab`,
      },
      {
        key: 'Headers',
        label: LanguageUtil.get({ id: 'common.tabs.headers', defaultMessage: 'Headers' }),
        content: <ApiHeadersComponent />,
        testid: `${ComponentName.API_VARIABLE}_headers_tab`,
      },
      {
        key: 'Body',
        label: LanguageUtil.get({ id: 'common.tabs.body', defaultMessage: 'Body' }),
        content: <ApiBodyComponent />,
        testid: `${ComponentName.API_VARIABLE}_body_tab`,
      },
    ];
  };
}

interface Props extends ReduxProps {}

class State {
  loading: boolean = true;
  transactions: TransactionJsonModel[] = [];
}

const mapStateToProps = (state: RootState) => ({
  apiVariable: state.apiVariable,
  auth: state.apiVariable.auth,
});

const mapDispatchToProps = { changeApiVariable, changeApiVariableAuth };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ApiEditorComponent);
